import React, { useState, useEffect, useContext, useMemo } from "react";

import { Formik, Form } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import { 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Select, 
  Typography, 
  Box, 
  Paper,
  Chip,
  Link,
  FormHelperText
} from "@material-ui/core";
import { 
  Visibility, 
  VisibilityOff, 
  Memory, 
  Android, 
  Security, 
  Settings,
  OpenInNew,
  AttachFile,
  Link as LinkIcon,
  CloudUpload,
  Fullscreen,
  FullscreenExit,
  Layers,
  Description
} from "@material-ui/icons";
import { InputAdornment, IconButton } from "@material-ui/core";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import PromptMediaPreview from "../PromptMediaPreview";
import PromptLayersManager from "../PromptLayersManager";
import PromptDocumentsManager from "../PromptDocumentsManager";
import {
  AI_PROVIDER_MENU_SECTIONS,
  DEFAULT_AI_PROVIDER,
  getApiKeyUi,
  getProviderLabel
} from "../../constants/aiProviderCatalog";

const useStyles = makeStyles(theme => ({
    root: {
        display: "flex",
        flexWrap: "wrap",
    },
    
    // ✅ ESTILOS PARA EL MODAL PROFESIONAL
    dialogTitle: {
        background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
        color: "white",
        padding: theme.spacing(2),
        "& .MuiTypography-h6": {
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
        },
    },
    
    // ✅ CORREGIDO: DialogContent dinámico según tema
    dialogContent: {
        padding: theme.spacing(2),
        backgroundColor: theme.mode === 'dark' ? '#1f2937' : "#fafbfc",
    },
    
    // ✅ CORREGIDO: FormSection dinámico según tema
    formSection: {
        backgroundColor: theme.palette.background.paper,
        borderRadius: "10px",
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
        boxShadow: theme.mode === 'dark' ? "0 1px 6px rgba(0,0,0,0.3)" : "0 1px 6px rgba(0,0,0,0.08)",
        border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e3f2fd'}`,
    },
    
    // ✅ CORREGIDO: SectionTitle dinámico según tema
    sectionTitle: {
        display: "flex",
        alignItems: "center",
        marginBottom: theme.spacing(1),
        color: theme.palette.primary.main,
        fontWeight: "bold",
        fontSize: "15px",
    },
    
    // ✅ CORREGIDO: FormField dinámico según tema
    formField: {
        marginBottom: theme.spacing(1.5),
        "& .MuiOutlinedInput-root": {
            borderRadius: "8px",
            backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
            color: theme.palette.text.primary,
            "& fieldset": {
                borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
            },
            "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
            },
            "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
            },
        },
        "& .MuiInputLabel-root": {
            color: theme.palette.text.secondary || theme.palette.text.primary,
        },
        "& .MuiInputLabel-root.Mui-focused": {
            color: theme.palette.primary.main,
        },
        "& .MuiFormHelperText-root": {
            color: theme.palette.text.secondary || theme.palette.text.primary,
        },
    },
    
    multFieldLine: {
        display: "flex",
        gap: theme.spacing(1.5),
        "& > *": {
            flex: 1,
        },
    },
    
    // ✅ ESTILOS PARA BOTONES
    btnWrapper: {
        position: "relative",
    },
    
    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    
    // ✅ CORREGIDO: CancelButton dinámico según tema
    cancelButton: {
        backgroundColor: theme.mode === 'dark' ? 'rgba(211, 47, 47, 0.2)' : "#ffebee",
        color: "#d32f2f",
        fontWeight: "bold",
        borderRadius: "8px",
        padding: "12px 24px",
        "&:hover": {
            backgroundColor: theme.mode === 'dark' ? 'rgba(211, 47, 47, 0.3)' : "#ffcdd2",
        },
    },
    
    saveButton: {
        background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
        fontWeight: "bold",
        borderRadius: "8px",
        padding: "12px 24px",
        boxShadow: "0 4px 15px rgba(25, 118, 210, 0.3)",
        "&:hover": {
            background: "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)",
            transform: "translateY(-2px)",
            boxShadow: "0 6px 20px rgba(25, 118, 210, 0.4)",
        },
    },
    
    // ✅ CORREGIDO: InfoBox dinámico según tema
    infoBox: {
        backgroundColor: theme.mode === 'dark' ? '#1e3a5f' : "#f0f8ff",
        border: `1px solid ${theme.mode === 'dark' ? '#2d4f7a' : '#e3f2fd'}`,
        borderRadius: "8px",
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(1.5),
    },
    
    // ✅ CORREGIDO: InfoText dinámico según tema
    infoText: {
        color: theme.palette.primary.main,
        fontSize: "13px",
        marginBottom: theme.spacing(0.75),
    },
    
    // ✅ CORREGIDO: LinkText dinámico según tema
    linkText: {
        color: theme.palette.primary.main,
        textDecoration: "none",
        fontWeight: "bold",
        "&:hover": {
            textDecoration: "underline",
        },
    },
    
    // ✅ ESTILOS PARA CHIPS DE PROVEEDOR
    providerChip: {
        backgroundColor: "#e8f5e8",
        color: "#2e7d32",
        fontWeight: "bold",
        margin: "4px",
    },
    
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
    },
    
    // ✅ CORREGIDO: FormControl dinámico según tema
    formControlDynamic: {
        "& .MuiOutlinedInput-root": {
            backgroundColor: theme.mode === 'dark' ? '#374151' : theme.palette.background.paper,
            color: theme.palette.text.primary,
            "& fieldset": {
                borderColor: theme.mode === 'dark' ? '#4B5563' : '#e0e0e0',
            },
            "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
            },
            "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
            },
        },
        "& .MuiInputLabel-root": {
            color: theme.palette.text.secondary || theme.palette.text.primary,
        },
        "& .MuiMenuItem-root": {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            "&:hover": {
                backgroundColor: theme.mode === 'dark' ? '#4B5563' : '#f5f5f5',
            },
        },
    },
    
    colorAdorment: {
        width: 20,
        height: 20,
    },
    
    // ✅ CORREGIDO: DialogActions dinámico según tema
    dialogActions: {
        padding: "16px 24px",
        backgroundColor: theme.mode === 'dark' ? '#1f2937' : "#f8f9fa",
        borderTop: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
    },
    
    // ✅ ESTILOS PARA EL MODAL AMPLIADO
    expandedPromptHelper: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginTop: theme.spacing(0.5),
        width: "100%",
    },
    
    expandedPromptButton: {
        marginLeft: theme.spacing(1),
        textTransform: "none",
        fontSize: "12px",
    },
    
    expandedPromptDialog: {
        "& .MuiDialog-paper": {
            maxWidth: "90vw",
            width: "90vw",
            maxHeight: "90vh",
            height: "90vh",
        },
    },
    
    expandedPromptContent: {
        padding: theme.spacing(3),
        height: "calc(90vh - 120px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
    },
    
    expandedPromptEditor: {
        flex: 1,
        marginTop: theme.spacing(2),
        minHeight: "300px",
        "& .MuiOutlinedInput-root": {
            height: "100%",
            alignItems: "flex-start",
            fontSize: "14px",
            lineHeight: "1.6",
            fontFamily: "'Courier New', monospace",
            backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#ffffff',
            color: theme.palette.text.primary,
            "& textarea": {
                height: "100% !important",
                overflow: "auto !important",
                padding: theme.spacing(2),
            },
        },
    },
    
    expandedPromptPreview: {
        flex: 1,
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: theme.mode === 'dark' ? '#1f2937' : '#f9fafb',
        borderRadius: "8px",
        border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e0e0e0'}`,
        overflow: "auto",
        fontSize: "14px",
        lineHeight: "1.8",
        whiteSpace: "pre-wrap",
        wordWrap: "break-word",
        fontFamily: "'Courier New', monospace",
        minHeight: "200px",
    },
}));



// Definir initialState fuera del componente para evitar recreación
const initialState = {
    name: "",
    prompt: "",
    voice: "texto", // ✅ POR DEFECTO SIEMPRE "TEXTO"
    voiceKey: "", // ✅ COMENTADO EN UI PERO MANTENIDO INTERNAMENTE
    voiceRegion: "", // ✅ COMENTADO EN UI PERO MANTENIDO INTERNAMENTE
    maxTokens: 400, // ✅ OPTIMIZADO: De 100 a 400 (respuestas completas)
    temperature: 0.5, // ✅ OPTIMIZADO: De 1.0 a 0.5 (balanceado)
    apiKey: "",
    queueId: "", // ✅ CAMBIADO: De null a string vacío (OPCIONAL)
    maxMessages: 25, // ✅ OPTIMIZADO: De 10 a 25 (contexto adecuado)
    provider: DEFAULT_AI_PROVIDER,
    workMode: "layers" // ✅ NUEVO: Modo de trabajo (layers o pdfs)
};

const PromptModal = ({ open, onClose, promptId, onSave }) => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);
    const [selectedVoice, setSelectedVoice] = useState("texto");
    const [showApiKey, setShowApiKey] = useState(true);
    const [selectedProvider, setSelectedProvider] = useState(DEFAULT_AI_PROVIDER);
    const [mediaFiles, setMediaFiles] = useState([]);
    const [loadingMedia, setLoadingMedia] = useState(false);
    const [urlModalOpen, setUrlModalOpen] = useState(false);
    const [isDragging, setIsDragging] = useState(null);
    const fileInputRef = React.useRef(null);
    const [validatingApi, setValidatingApi] = useState(false);
    const [expandedPromptModalOpen, setExpandedPromptModalOpen] = useState(false);
    const [expandedPromptText, setExpandedPromptText] = useState("");
    const [expandedPromptChanged, setExpandedPromptChanged] = useState(false);
    const [apiValidated, setApiValidated] = useState(false);
    const [apiValidationError, setApiValidationError] = useState(null);
    // ✅ Estado para mensajes informativos en el modal (no toast)
    const [mediaMessage, setMediaMessage] = useState({ type: null, text: null }); // type: 'success' | 'error' | null
    /** GET detalle `/prompt/:id` envía apiKey literal (misma empresa) para edición / probar conexión §0290. */
    const [hasStoredApiKey, setHasStoredApiKey] = useState(false);
    const [savedProvider, setSavedProvider] = useState(null);
    const [selectedWorkMode, setSelectedWorkMode] = useState('layers'); // 'layers' o 'pdfs'

    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                name: Yup.string().required(i18n.t("promptModal.validation.nameRequired")),
                apiKey: Yup.string().test(
                    "api-required-or-masked-on-server",
                    i18n.t("promptModal.validation.apiKeyRequired"),
                    val => {
                        if (hasStoredApiKey && !(val && String(val).trim()))
                            return true;
                        return !!(val && String(val).trim());
                    }
                ),
                prompt: Yup.string().required(i18n.t("promptModal.validation.promptRequired")),
                maxTokens: Yup.number().min(1).max(4000).required(i18n.t("promptModal.validation.maxTokensRequired")),
                temperature: Yup.number().min(0).max(2).required(i18n.t("promptModal.validation.temperatureRequired")),
                maxMessages: Yup.number().min(1).max(100).required(i18n.t("promptModal.validation.maxMessagesRequired")),
                queueId: Yup.string().nullable().optional()
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [i18n.language, hasStoredApiKey]
    );

    const handleToggleApiKey = () => {
        setShowApiKey(!showApiKey);
    };

    const handleChangeProvider = (e, currentApiKeyValue) => {
        const newProvider = e.target.value;
        setSelectedProvider(newProvider);
        setApiValidationError(null);

        const keyEmpty = !(currentApiKeyValue && String(currentApiKeyValue).trim());
        if (newProvider === savedProvider && hasStoredApiKey && keyEmpty) {
            setApiValidated(true);
        } else if (newProvider !== savedProvider) {
            setApiValidated(false);
        }
    };

    const [prompt, setPrompt] = useState(initialState);

    useEffect(() => {
        // ✅ Resetear estado cuando se abre el modal sin promptId
        if (!open) {
            return;
        }
        
        const fetchPrompt = async () => {
            if (!promptId) {
                console.log("[PromptModal] No hay promptId, reseteando a valores iniciales");
                setPrompt(initialState);
                setMediaFiles([]);
                setSelectedWorkMode(initialState.workMode || "layers"); // ✅ Resetear workMode al valor inicial
                setSelectedProvider(initialState.provider || DEFAULT_AI_PROVIDER);
                setHasStoredApiKey(false);
                setSavedProvider(null);
                setApiValidated(false);
                setApiValidationError(null);
                return;
            }
            try {
                console.log("[PromptModal] Cargando prompt con ID:", promptId);
                const { data } = await api.get(`/prompt/${promptId}`);
                console.log("[PromptModal] Datos recibidos del backend:", {
                    id: data.id,
                    name: data.name,
                    workMode: data.workMode,
                    workModeType: typeof data.workMode,
                    workModeValue: data.workMode,
                    allData: data
                });
                
                setPrompt(prevState => {
                    // ✅ CONVERTIR queueId null A STRING VACÍO PARA EVITAR WARNINGS
                    const processedData = {
                        ...data,
                        queueId: data.queueId || "", // Si es null, undefined o 0, usar string vacío
                        apiKey:
                            data.apiKey !== undefined && data.apiKey !== null
                                ? String(data.apiKey)
                                : "",
                    };
                    return { ...prevState, ...processedData };
                });
                setSelectedVoice(data.voice);
                setSelectedProvider(data.provider || DEFAULT_AI_PROVIDER);
                
                // ✅ Cargar workMode del backend - corregido: data ya es el prompt completo
                // Asegurarse de que workMode sea válido ('layers' o 'pdfs')
                // Normalizar a minúsculas para evitar problemas de case sensitivity
                const normalizedWorkMode = String(data.workMode || "layers").toLowerCase();
                const workModeValue = (normalizedWorkMode === 'layers' || normalizedWorkMode === 'pdfs') 
                    ? normalizedWorkMode 
                    : "layers";
                console.log("[PromptModal] Estableciendo workMode:", workModeValue, "desde data.workMode:", data.workMode, "normalized:", normalizedWorkMode);
                setSelectedWorkMode(workModeValue);
                
                // ✅ Guardar API Key y proveedor originales para comparar cambios
                setSavedProvider(data.provider || DEFAULT_AI_PROVIDER);

                const hasKey = Boolean(data.hasApiKey);
                setHasStoredApiKey(hasKey);

                /** Lista sigue ocultando clave; en edición llega valor en `apiKey`. */
                if (hasKey && data.provider) {
                    setApiValidated(true);
                    setApiValidationError(null);
                }
                
                // ✅ Cargar archivos multimedia
                await fetchMediaFiles(promptId);
            } catch (err) {
                toastError(err);
            }
        };

        fetchPrompt();
    }, [promptId, open]); // Removido initialState de las dependencias

    // ✅ Función para cargar archivos multimedia (retorna el array directamente)
    const fetchMediaFiles = async (id) => {
        try {
            console.log("🔵 [fetchMediaFiles] Cargando archivos para promptId:", id);
            // NO cambiar loadingMedia aquí para evitar conflictos con otras operaciones
            const { data } = await api.get(`/prompt/${id}/media`);
            const files = data || [];
            
            // ✅ Limpiar y normalizar datos recibidos del backend
            const cleanedFiles = files.map(file => {
                // ✅ Limpiar mediaName: eliminar cualquier carácter no imprimible o espacios extra
                let cleanName = file.mediaName;
                if (cleanName) {
                    cleanName = String(cleanName)
                        .replace(/[\x00-\x1F\x7F-\x9F]/g, '') // Eliminar caracteres de control
                        .replace(/\0/g, '') // Eliminar null bytes
                        .trim();
                }
                
                // ✅ Limpiar mediaSize: asegurar que sea número
                let cleanSize = file.mediaSize;
                if (cleanSize) {
                    if (typeof cleanSize === 'string') {
                        cleanSize = parseInt(cleanSize.replace(/[^0-9]/g, ''), 10); // Solo números
                    }
                    cleanSize = Number(cleanSize);
                    if (isNaN(cleanSize)) cleanSize = null;
                }
                
                return {
                    ...file,
                    mediaName: cleanName || null,
                    mediaSize: cleanSize
                };
            });
            
            setMediaFiles(cleanedFiles);
            return cleanedFiles;
        } catch (err) {
            console.error("❌ [fetchMediaFiles] Error cargando archivos multimedia:", err);
            setMediaFiles([]);
            return []; // ✅ Retornar array vacío en caso de error
        }
    };

    // ✅ Función para subir archivo local (ahora soporta múltiples archivos)
    const handleFileUpload = async (files) => {
        console.log("🔵 [handleFileUpload] Iniciando upload, promptId:", promptId, "files:", files);
        console.log("🔵 [handleFileUpload] files.length:", files?.length, "files type:", typeof files);
        
        if (!promptId) {
            console.error("❌ [handleFileUpload] No hay promptId");
            setMediaMessage({ type: 'error', text: i18n.t("promptModal.media.savePromptFirst") });
            return;
        }

        // ✅ CRÍTICO: Convertir FileList a Array INMEDIATAMENTE (antes de cualquier async)
        let filesArray = [];
        if (files && files.length > 0) {
            // Si es FileList, convertirlo a Array
            if (files instanceof FileList) {
                filesArray = Array.from(files);
            } else if (Array.isArray(files)) {
                filesArray = files;
            } else {
                console.error("❌ [handleFileUpload] Tipo de archivo no reconocido:", typeof files);
                setMediaMessage({ type: 'error', text: i18n.t("promptModal.media.unknownFileType") });
                return;
            }
        } else {
            console.error("❌ [handleFileUpload] No hay archivos para subir");
            setMediaMessage({ type: 'error', text: i18n.t("promptModal.media.noFilesSelected") });
            return;
        }
        
        console.log("✅ [handleFileUpload] Archivos convertidos a Array:", filesArray.length);
        console.log("✅ [handleFileUpload] Nombres de archivos:", filesArray.map(f => f.name));

        try {
            // ✅ CRÍTICO: Recargar archivos desde el backend ANTES de validar (estado real)
            console.log("🔵 [handleFileUpload] Recargando archivos desde BD...");
            const currentFiles = await fetchMediaFiles(promptId);
            console.log("✅ [handleFileUpload] Archivos actuales en BD:", currentFiles.length);

        // ✅ Validar que no se excedan 6 archivos en total (usando valor retornado directamente)
        const totalAfterUpload = currentFiles.length + filesArray.length;
        if (totalAfterUpload > 6) {
            setMediaMessage({ 
                type: 'error', 
                text: i18n.t("promptModal.media.maxSixWithCounts", {
                    current: currentFiles.length,
                    adding: filesArray.length,
                    remaining: Math.max(0, 6 - currentFiles.length),
                }),
            });
            return;
        }

        // Validar cada archivo
        const validFiles = [];
        const errors = [];

        for (const file of filesArray) {
            // Validar tamaño según tipo
            const fileType = file.type.split('/')[0];
            const maxSizes = {
                image: 10 * 1024 * 1024, // 10 MB
                video: 15 * 1024 * 1024, // 15 MB
                audio: 10 * 1024 * 1024, // 10 MB
                application: 15 * 1024 * 1024, // 15 MB (documentos)
            };

            const maxSize = maxSizes[fileType] || 15 * 1024 * 1024;
            if (file.size > maxSize) {
                const maxMB = maxSize / (1024 * 1024);
                errors.push(i18n.t("promptModal.media.fileTooBig", { name: file.name, maxMb: maxMB }));
                continue;
            }

            validFiles.push(file);
        }

        // Mostrar errores si hay
        if (errors.length > 0) {
            setMediaMessage({ 
                type: 'error', 
                text: i18n.t("promptModal.media.someRejected", { list: errors.join(", ") }),
            });
        }

        if (validFiles.length === 0) {
            return;
        }

            // Subir archivos uno por uno (recargando estado después de cada uno)
            setLoadingMedia(true);
            setMediaMessage({ type: null, text: null }); // Limpiar mensaje anterior
            const uploadedFiles = [];
            const failedFiles = [];

            console.log("🔵 [handleFileUpload] Iniciando subida de", validFiles.length, "archivos válidos");

            for (let i = 0; i < validFiles.length; i++) {
                const file = validFiles[i];
                console.log(`🔵 [handleFileUpload] Procesando archivo ${i + 1}/${validFiles.length}:`, file.name);
                
                // ✅ Recargar estado actual desde BD antes de cada validación
                const currentFiles = await fetchMediaFiles(promptId);
                
                // ✅ Validar usando el valor retornado directamente (no el estado)
                if (currentFiles.length >= 6) {
                    console.warn("⚠️ [handleFileUpload] Límite alcanzado:", currentFiles.length);
                    setMediaMessage({ 
                        type: 'error', 
                        text: i18n.t("promptModal.media.maxSixNoMore", { count: currentFiles.length }),
                    });
                    break;
                }

                const formData = new FormData();
                formData.append("file", file);
                
                try {
                    console.log(`🔵 [handleFileUpload] Enviando archivo ${file.name} al servidor...`);
                    const { data } = await api.post(`/prompt/${promptId}/media`, formData, {
                        headers: {
                            "Content-Type": "multipart/form-data",
                        },
                    });
                    
                    console.log(`✅ [handleFileUpload] Archivo ${file.name} subido exitosamente:`, data);
                    uploadedFiles.push(data);
                    
                    // ✅ Recargar archivos desde BD después de cada éxito
                    await fetchMediaFiles(promptId);
                } catch (fileError) {
                    // Si falla un archivo, continuar con los demás
                    const errorMsg = fileError.response?.data?.message || fileError.message || i18n.t("promptModal.apiTest.unknownError");
                    console.error(`❌ [handleFileUpload] Error subiendo ${file.name}:`, fileError);
                    failedFiles.push({ name: file.name, error: errorMsg });
                }
            }
            
            console.log("🔵 [handleFileUpload] Proceso completado. Exitosos:", uploadedFiles.length, "Fallidos:", failedFiles.length);
            
            // ✅ Mostrar mensajes en el modal
            if (uploadedFiles.length > 0 && failedFiles.length === 0) {
                setMediaMessage({ 
                    type: 'success', 
                    text: i18n.t("promptModal.media.uploadSuccessCount", { count: uploadedFiles.length }),
                });
            } else if (uploadedFiles.length > 0 && failedFiles.length > 0) {
                setMediaMessage({ 
                    type: 'error', 
                    text: i18n.t("promptModal.media.uploadPartial", {
                        uploaded: uploadedFiles.length,
                        failed: failedFiles.length,
                        names: failedFiles.map(f => f.name).join(", "),
                    }),
                });
            } else if (failedFiles.length > 0) {
                setMediaMessage({ 
                    type: 'error', 
                    text: i18n.t("promptModal.media.uploadFailedDetail", {
                        details: failedFiles.map(f => `${f.name} (${f.error})`).join(", "),
                    }),
                });
            } else if (uploadedFiles.length === 0 && failedFiles.length === 0) {
                setMediaMessage({ 
                    type: 'error', 
                    text: i18n.t("promptModal.media.uploadNone"),
                });
            }
        } catch (err) {
            console.error("❌ [handleFileUpload] Error inesperado:", err);
            setMediaMessage({ 
                type: 'error', 
                text: i18n.t("promptModal.media.unexpectedError", {
                    message: err.message || i18n.t("promptModal.apiTest.unknownError"),
                }),
            });
        } finally {
            setLoadingMedia(false);
            console.log("🔵 [handleFileUpload] Finalizado");
        }
    };

    // ✅ Función para eliminar archivo
    const handleDeleteMedia = async (mediaId) => {
        if (!promptId) return;
        
        try {
            setLoadingMedia(true);
            setMediaMessage({ type: null, text: null }); // Limpiar mensaje
            await api.delete(`/prompt/${promptId}/media/${mediaId}`);
            await fetchMediaFiles(promptId); // ✅ Recargar desde BD
            setMediaMessage({ type: 'success', text: i18n.t("promptModal.media.fileDeleted") });
        } catch (err) {
            setMediaMessage({ 
                type: 'error', 
                text: i18n.t("promptModal.media.deleteFileError", {
                    message: err.response?.data?.message || err.message || i18n.t("promptModal.apiTest.unknownError"),
                }),
            });
        } finally {
            setLoadingMedia(false);
        }
    };

    // ✅ Función para agregar URL externa
    const handleAddUrl = async (url, caption) => {
        if (!promptId) {
            setMediaMessage({ type: 'error', text: i18n.t("promptModal.media.savePromptFirst") });
            return;
        }

        // ✅ Recargar estado actual desde BD
        const currentFiles = await fetchMediaFiles(promptId);

        if (currentFiles.length >= 6) {
            setMediaMessage({ type: 'error', text: i18n.t("promptModal.media.maxSixFiles") });
            return;
        }

        try {
            setLoadingMedia(true);
            setMediaMessage({ type: null, text: null }); // Limpiar mensaje
            const { data } = await api.post(`/prompt/${promptId}/media/url`, {
                mediaUrl: url,
                caption: caption || null,
            });
            await fetchMediaFiles(promptId); // ✅ Recargar desde BD
            setMediaMessage({ type: 'success', text: i18n.t("promptModal.media.urlAdded") });
            setUrlModalOpen(false);
        } catch (err) {
            setMediaMessage({ 
                type: 'error', 
                text: i18n.t("promptModal.media.urlAddError", {
                    message: err.response?.data?.message || err.message || i18n.t("promptModal.apiTest.unknownError"),
                }),
            });
        } finally {
            setLoadingMedia(false);
        }
    };

    // ✅ Función para reordenar (drag & drop básico)
    const handleReorder = (index, action) => {
        if (action === "start") {
            setIsDragging(index);
        } else if (action === "end") {
            setIsDragging(null);
        }
    };

      const handleClose = () => {
    setPrompt(initialState);
    setSelectedVoice("texto"); // ✅ SIEMPRE "TEXTO" POR DEFECTO
    setSelectedProvider(DEFAULT_AI_PROVIDER);
    setSelectedWorkMode("layers"); // ✅ Resetear workMode al cerrar
    setMediaFiles([]);
    setUrlModalOpen(false);
    setIsDragging(null);
    setApiValidated(false);
    setApiValidationError(null);
    // ✅ Limpiar valores guardados al cerrar
    setHasStoredApiKey(false);
    setSavedProvider(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onClose();
  };

  // ✅ Función para validar API Key y proveedor
  const handleValidateApi = async (currentApiKey) => {
    // ✅ CRÍTICO: Usar SOLO el valor pasado como parámetro (valor actual del formulario)
    // NO usar prompt.apiKey (estado guardado) - esto asegura que validamos lo que el usuario escribió
    if (!currentApiKey || !currentApiKey.trim()) {
      toast.error(i18n.t("promptModal.apiTest.enterApiKeyFirst"));
      return;
    }
    
    const apiKey = currentApiKey.trim();
    const provider = selectedProvider;

    if (!provider) {
      toast.error(i18n.t("promptModal.apiTest.selectProviderFirst"));
      return;
    }

    // ✅ Validar formato básico de API Key
    if (apiKey.length < 20) {
      toast.error(i18n.t("promptModal.apiTest.keyTooShortToast"));
      setApiValidated(false);
      setApiValidationError(i18n.t("promptModal.apiTest.keyTooShort"));
      return;
    }

    try {
      setValidatingApi(true);
      setApiValidationError(null);
      setApiValidated(false);

      console.log(`[ValidateAPI] Validando API Key (primeros 10 chars): ${apiKey.substring(0, 10)}..., Proveedor: ${provider}`);
      console.log(`[ValidateAPI] Longitud de API Key: ${apiKey.length} caracteres`);

      const { data } = await api.post("/prompt/validate-api", {
        apiKey: apiKey, // ✅ Usar el valor exacto que el usuario ingresó
        provider: provider
      });

      if (data.success) {
        setApiValidated(true);
        setApiValidationError(null);
        toast.success(`✅ ${data.message || i18n.t("promptModal.apiTest.connectionSuccess")}`);
      } else {
        setApiValidated(false);
        setApiValidationError(data.message || i18n.t("promptModal.apiTest.validationFailed"));
        toast.error(data.message || i18n.t("promptModal.apiTest.validateError"));
      }
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || i18n.t("promptModal.apiTest.validateError");
      setApiValidated(false);
      setApiValidationError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setValidatingApi(false);
    }
  };

  // ✅ ELIMINADA FUNCIÓN NO UTILIZADA: handleChangeVoice

  const handleSavePrompt = async values => {
        // ✅ VALIDAR API ANTES DE GUARDAR
        const trimmedKey = values.apiKey ? String(values.apiKey).trim() : "";
        const okPreserveStoredCredential =
            hasStoredApiKey &&
            !trimmedKey &&
            selectedProvider === savedProvider;

        if (!apiValidated && !okPreserveStoredCredential) {
            toast.error(
                hasStoredApiKey
                    ? i18n.t("promptModal.mustValidateBeforeSaveWithStored")
                    : i18n.t("promptModal.mustValidateBeforeSave")
            );
            return;
        }

        const promptData = { ...values, voice: selectedVoice, provider: selectedProvider, workMode: selectedWorkMode };
        console.log("[PromptModal] Guardando prompt con workMode:", selectedWorkMode, "promptData:", promptData);
        // ✅ ELIMINADA VALIDACIÓN OBLIGATORIA: Permitir prompts sin departamento
        // if (!values.queueId) {
        //     toastError("Informe o setor");
        //     return;
        // }
        try {
            let savedPromptId = promptId;
            let savedResp;
            if (promptId) {
                const { data } = await api.put(`/prompt/${promptId}`, promptData);
                savedResp = data;
            } else {
                const { data } = await api.post("/prompt", promptData);
                savedResp = data;
                savedPromptId = data.id;
            }

            if (savedResp && typeof savedResp.hasApiKey !== "undefined")
                setHasStoredApiKey(!!savedResp.hasApiKey);
            setSavedProvider(selectedProvider);
            
            toast.success(i18n.t("promptModal.success"));
            
            // ✅ Si es un prompt nuevo, recargar archivos multimedia
            if (!promptId && savedPromptId) {
                await fetchMediaFiles(savedPromptId);
            }
            
            // ✅ AGREGAR CALLBACK PARA REFRESCAR LISTA
            if (onSave) {
                onSave();
            }
        } catch (err) {
            toastError(err);
        }
        // ✅ NO cerrar el modal si es un prompt nuevo (para permitir agregar multimedia)
        if (promptId) {
        handleClose();
        }
    };

    return (
        <div className={classes.root}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                scroll="paper"
                fullWidth={false}
                PaperProps={{ style: { width: "840px", maxWidth: "90vw" } }}
            >
                <DialogTitle className={classes.dialogTitle}>
                    <Box display="flex" alignItems="center">
                        <Memory style={{ marginRight: 12, fontSize: 28 }} />
                        <Typography variant="h6">
                            {promptId
                                ? `${i18n.t("promptModal.title.edit")}`
                                : `${i18n.t("promptModal.title.add")}`}
                        </Typography>
                    </Box>
                </DialogTitle>
                
                <Formik
                    initialValues={prompt}
                    enableReinitialize={true}
                    validationSchema={validationSchema}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSavePrompt(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ isSubmitting, errors, touched, values, setFieldValue }) => {
                        // ✅ Guardar referencia a setFieldValue para usarla en el modal ampliado
                        window.setFieldValuePrompt = setFieldValue;
                        const apiKeyUi = getApiKeyUi(selectedProvider);
                        
                        return (
                        <Form style={{ width: "100%" }}>
                            <DialogContent className={classes.dialogContent}>
                                
                                {/* ✅ INFORMACIÓN SOBRE PROVEEDOR DE IA */}
                                <Paper className={classes.infoBox}>
                                    <Box display="flex" alignItems="center" marginBottom={1}>
                                        <Android style={{ marginRight: 8, color: "#1976d2" }} />
                                        <Typography variant="h6" style={{ color: "#1976d2", fontWeight: "bold" }}>
                                            <span role="img" aria-label="robot">🤖</span> {i18n.t("promptModal.providerInfo.title")}
                                        </Typography>
                                    </Box>
                                    <Typography className={classes.infoText}>
                                        {i18n.t("promptModal.providerInfo.body1")}
                                    </Typography>
                                    <Typography className={classes.infoText}>
                                        {i18n.t("promptModal.providerInfo.body2")}
                                    </Typography>
                                </Paper>
                                
                                {/* ✅ SECCIÓN: CONFIGURACIÓN DE API */}
                                <Paper className={classes.formSection}>
                                    <Typography className={classes.sectionTitle}>
                                        <Security style={{ marginRight: 8 }} />
                                        {i18n.t("promptModal.sections.api")}
                                    </Typography>
                                    
                                    <FormControl fullWidth margin="dense" variant="outlined" className={classes.formField}>
                                        <TextField
                                            label={apiKeyUi.label}
                                            name="apiKey"
                                            value={values.apiKey || ""}
                                            onChange={(e) => {
                                                const newApiKey = e.target.value;
                                                setFieldValue("apiKey", newApiKey);
                                                const t = newApiKey.trim();
                                                if (t.length > 0) {
                                                    setApiValidated(false);
                                                    setApiValidationError(null);
                                                } else if (
                                                    hasStoredApiKey &&
                                                    selectedProvider === savedProvider
                                                ) {
                                                    setApiValidated(true);
                                                    setApiValidationError(null);
                                                } else {
                                                    setApiValidated(false);
                                                }
                                            }}
                                            type={showApiKey ? "text" : "password"}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            error={
                                                (touched.apiKey && Boolean(errors.apiKey)) ||
                                                (apiValidationError && !apiValidated)
                                            }
                                            helperText={
                                                (touched.apiKey && errors.apiKey) ||
                                                (apiValidationError && !apiValidated
                                                    ? apiValidationError
                                                    : "") ||
                                                (apiValidated
                                                    ? i18n.t("promptModal.apiTest.validatedHelper")
                                                    : [
                                                          hasStoredApiKey &&
                                                          !(values.apiKey && String(values.apiKey).trim())
                                                              ? i18n.t(
                                                                    "promptModal.apiTest.storedKeyHelper"
                                                                )
                                                              : "",
                                                          apiKeyUi.helper
                                                      ]
                                                          .filter(Boolean)
                                                          .join(" "))
                                            }
                                            placeholder={
                                                hasStoredApiKey &&
                                                !(values.apiKey && String(values.apiKey).trim())
                                                    ? i18n.t(
                                                          "promptModal.apiTest.storedKeyMaskPlaceholder"
                                                      )
                                                    : apiKeyUi.placeholder
                                            }
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={handleToggleApiKey}>
                                                            {showApiKey ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </FormControl>
                                    
                                    <FormControl fullWidth margin="dense" variant="outlined" className={`${classes.formField} ${classes.formControlDynamic}`}>
                                        <InputLabel>{i18n.t("promptModal.fields.aiProvider")}</InputLabel>
                                        <Select
                                            id="provider-select"
                                            labelWidth={120}
                                            name="provider"
                                            label={i18n.t("promptModal.fields.aiProvider")}
                                            value={selectedProvider}
                                            onChange={(e) => {
                                                const v = e.target.value;
                                                setFieldValue("provider", v);
                                                handleChangeProvider(e, values.apiKey);
                                            }}
                                            multiple={false}
                                        >
                                            {AI_PROVIDER_MENU_SECTIONS.flatMap(
                                                (section, sectionIdx) => [
                                                    <MenuItem
                                                        key={`section-${section.header}`}
                                                        disabled
                                                        style={{
                                                            backgroundColor: "#f5f5f5",
                                                            fontWeight: "bold",
                                                            color: "#333",
                                                            marginTop:
                                                                sectionIdx > 0 ? "8px" : 0
                                                        }}
                                                    >
                                                        {section.header}
                                                    </MenuItem>,
                                                    ...section.items.map((item) => (
                                                        <MenuItem
                                                            key={item.value}
                                                            value={item.value}
                                                        >
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                            >
                                                                <Chip
                                                                    label={item.chip}
                                                                    size="small"
                                                                    className={
                                                                        item.useProviderChipClass
                                                                            ? classes.providerChip
                                                                            : undefined
                                                                    }
                                                                    style={
                                                                        item.chipStyle ||
                                                                        undefined
                                                                    }
                                                                />
                                                                <Typography
                                                                    style={{ marginLeft: 8 }}
                                                                >
                                                                    {getProviderLabel(
                                                                        item.value
                                                                    )}
                                                                </Typography>
                                                            </Box>
                                                        </MenuItem>
                                                    ))
                                                ]
                                            )}
                                        </Select>
                                    </FormControl>
                                    
                                    {/* ✅ Botón de validación debajo del Proveedor de IA */}
                                    <Box marginTop={1} display="flex" justifyContent="flex-end">
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            color={apiValidated ? "primary" : "default"}
                                            onClick={() => handleValidateApi(values.apiKey)}
                                            disabled={
                                                validatingApi ||
                                                !selectedProvider ||
                                                !(values.apiKey && String(values.apiKey).trim())
                                            }
                                            startIcon={validatingApi ? <CircularProgress size={16} /> : (apiValidated ? <Security style={{ color: "green" }} /> : <Security />)}
                                        >
                                            {validatingApi ? i18n.t("promptModal.apiTest.validating") : apiValidated ? i18n.t("promptModal.apiTest.validated") : i18n.t("promptModal.apiTest.testConnection")}
                                        </Button>
                                    </Box>
                                </Paper>
                                
                                {/* ✅ SECCIÓN: INFORMACIÓN BÁSICA */}
                                <Paper className={classes.formSection}>
                                    <Typography className={classes.sectionTitle}>
                                        <Memory style={{ marginRight: 8 }} />
                                        {i18n.t("promptModal.sections.basic")}
                                    </Typography>
                                    
                                    <TextField
                                        label={i18n.t("promptModal.fields.promptName")}
                                        name="name"
                                        value={values.name || ""}
                                        onChange={(e) => setFieldValue('name', e.target.value)}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        className={classes.formField}
                                        error={touched.name && Boolean(errors.name)}
                                        helperText={(touched.name && errors.name) || i18n.t("promptModal.fields.promptNameHelper")}
                                        placeholder={i18n.t("promptModal.fields.promptNamePlaceholder")}
                                    />
                                    
                                    <TextField
                                        label={i18n.t("promptModal.fields.mainPrompt")}
                                        name="prompt"
                                        value={values.prompt || ""}
                                        onChange={(e) => setFieldValue('prompt', e.target.value)}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        className={classes.formField}
                                        rows={6}
                                        multiline={true}
                                        error={touched.prompt && Boolean(errors.prompt)}
                                        helperText={(touched.prompt && errors.prompt) || i18n.t("promptModal.fields.mainPromptHelper")}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        color="primary"
                                                        startIcon={<Fullscreen />}
                                                        className={classes.expandedPromptButton}
                                                        onClick={() => {
                                                            setExpandedPromptText(values.prompt || "");
                                                            setExpandedPromptChanged(false);
                                                            setExpandedPromptModalOpen(true);
                                                        }}
                                                        style={{ fontSize: '11px', padding: '4px 8px', marginRight: 8 }}
                                                    >
                                                        {i18n.t("promptModal.buttons.expand")}
                                                    </Button>
                                                </InputAdornment>
                                            )
                                        }}
                                        placeholder={i18n.t("promptModal.fields.mainPromptPlaceholder")}
                                    />
                                    <Box display="flex" justifyContent="flex-end" marginTop={0.5}>
                                        <Typography variant="caption" color="textSecondary">
                                            {i18n.t("promptModal.fields.charCount", { count: (values.prompt || "").length })}
                                        </Typography>
                                    </Box>
                                </Paper>
                                
                                {/* ✅ SECCIÓN: CONFIGURACIÓN AVANZADA */}
                                <Paper className={classes.formSection}>
                                    <Typography className={classes.sectionTitle}>
                                        <Settings style={{ marginRight: 8 }} />
                                        {i18n.t("promptModal.sections.advanced")}
                                    </Typography>
                                    
                                    <div className={classes.multFieldLine}>
                                        <TextField
                                            label={i18n.t("promptModal.fields.temperature")}
                                            name="temperature"
                                            value={values.temperature || ""}
                                            onChange={(e) => setFieldValue('temperature', e.target.value)}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            type="number"
                                            className={classes.formField}
                                            error={touched.temperature && Boolean(errors.temperature)}
                                            helperText={(touched.temperature && errors.temperature) || i18n.t("promptModal.fields.temperatureHelper")}
                                            inputProps={{ min: 0, max: 2, step: 0.1 }}
                                        />
                                        <TextField
                                            label={i18n.t("promptModal.fields.maxTokens")}
                                            name="maxTokens"
                                            value={values.maxTokens || ""}
                                            onChange={(e) => setFieldValue('maxTokens', e.target.value)}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            type="number"
                                            className={classes.formField}
                                            error={touched.maxTokens && Boolean(errors.maxTokens)}
                                            helperText={(touched.maxTokens && errors.maxTokens) || i18n.t("promptModal.fields.maxTokensHelper")}
                                            inputProps={{ min: 1, max: 4000 }}
                                        />
                                        <TextField
                                            label={i18n.t("promptModal.fields.maxMessages")}
                                            name="maxMessages"
                                            value={values.maxMessages || ""}
                                            onChange={(e) => setFieldValue('maxMessages', e.target.value)}
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            type="number"
                                            className={classes.formField}
                                            error={touched.maxMessages && Boolean(errors.maxMessages)}
                                            helperText={(touched.maxMessages && errors.maxMessages) || i18n.t("promptModal.fields.maxMessagesHelper")}
                                            inputProps={{ min: 1, max: 100 }}
                                        />
                                    </div>
                                    
                                    <FormControl fullWidth margin="dense" variant="outlined" className={`${classes.formField} ${classes.formControlDynamic}`}>
                                        <InputLabel>{i18n.t("promptModal.fields.queueOptional")}</InputLabel>
                                        <Select
                                            label={i18n.t("promptModal.fields.queueOptional")}
                                            name="queueId"
                                            variant="outlined"
                                            margin="dense"
                                            fullWidth
                                            value={values.queueId || ""}
                                            onChange={(e) => setFieldValue("queueId", e.target.value)}
                                        >
                                            <MenuItem value="">
                                                <em>{i18n.t("promptModal.fields.noQueue")}</em>
                                            </MenuItem>
                                            {Array.isArray(user?.queues) && user.queues.map((queue) => (
                                                <MenuItem key={queue.id} value={queue.id}>
                                                    {queue.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Paper>
                                
                                {/* ✅ SECCIÓN: ARCHIVOS MULTIMEDIA */}
                                <Paper className={classes.formSection}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom={1}>
                                        <Typography className={classes.sectionTitle}>
                                            <AttachFile style={{ marginRight: 8 }} />
                                            {i18n.t("promptModal.sections.media")}
                                        </Typography>
                                        <Box display="flex" gap={1}>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<LinkIcon />}
                                                onClick={() => {
                                                    if (!promptId) {
                                                        toast.error(i18n.t("promptModal.media.savePromptFirstToast"));
                                                        return;
                                                    }
                                                    setUrlModalOpen(true);
                                                }}
                                                disabled={loadingMedia || mediaFiles.length >= 6}
                                            >
                                                {i18n.t("promptModal.media.addUrl")}
                                            </Button>
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                multiple
                                                accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                                                style={{ display: "none" }}
                                                onChange={(e) => {
                                                    const files = e.target.files;
                                                    console.log("🔵 [onChange] Archivos seleccionados:", files?.length);
                                                    
                                                    if (files && files.length > 0) {
                                                        // ✅ Convertir FileList a Array ANTES de pasar a la función
                                                        const filesArray = Array.from(files);
                                                        console.log("✅ [onChange] Archivos convertidos:", filesArray.length);
                                                        
                                                        // ✅ Limpiar el input DESPUÉS de procesar (no antes)
                                                        e.target.value = "";
                                                        
                                                        // ✅ Llamar a la función con el array
                                                        handleFileUpload(filesArray);
                                                    } else {
                                                        console.warn("⚠️ [onChange] No se seleccionaron archivos");
                                                    }
                                                }}
                                            />
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<CloudUpload />}
                                                onClick={() => {
                                                    if (!promptId) {
                                                        toast.error(i18n.t("promptModal.media.savePromptFirstToast"));
                                                        return;
                                                    }
                                                    fileInputRef.current?.click();
                                                }}
                                                disabled={loadingMedia || mediaFiles.length >= 6}
                                            >
                                                {i18n.t("promptModal.media.uploadFile")}
                                            </Button>
                                        </Box>
                                    </Box>
                                    
                                    {/* ✅ Mensaje informativo/error en el modal (no toast) */}
                                    {mediaMessage.text && (
                                        <Box 
                                            style={{ 
                                                marginBottom: 16,
                                                padding: 12,
                                                borderRadius: 4,
                                                backgroundColor: mediaMessage.type === 'error' ? '#ffebee' : '#e8f5e9',
                                                border: `1px solid ${mediaMessage.type === 'error' ? '#f44336' : '#4caf50'}`,
                                                color: mediaMessage.type === 'error' ? '#c62828' : '#2e7d32'
                                            }}
                                        >
                                            <Typography style={{ fontSize: "13px", fontWeight: 500 }}>
                                                {mediaMessage.type === 'error' ? '❌' : '✅'} {mediaMessage.text}
                                            </Typography>
                                        </Box>
                                    )}
                                    
                                    <Box className={classes.infoBox} style={{ marginBottom: 16 }}>
                                        <Typography className={classes.infoText} style={{ fontSize: "12px" }}>
                                            {i18n.t("promptModal.media.hintFiles")}
                                        </Typography>
                                        <Typography className={classes.infoText} style={{ fontSize: "12px" }}>
                                            {i18n.t("promptModal.media.hintLimits")}
                                        </Typography>
                                        <Typography className={classes.infoText} style={{ fontSize: "12px" }}>
                                            {i18n.t("promptModal.media.hintSyntax")}
                                        </Typography>
                                    </Box>
                                    
                                    {loadingMedia ? (
                                        <Box display="flex" justifyContent="center" padding={3}>
                                            <CircularProgress size={24} />
                                        </Box>
                                    ) : (
                                        <PromptMediaPreview
                                            mediaFiles={mediaFiles}
                                            onDelete={handleDeleteMedia}
                                            onReorder={handleReorder}
                                            isDragging={isDragging}
                                        />
                                    )}
                                    
                                    {mediaFiles.length > 0 && (
                                        <Box marginTop={1}>
                                            <Typography variant="caption" color="textSecondary">
                                                {i18n.t("promptModal.media.filesCount", { current: mediaFiles.length })}
                                            </Typography>
                                        </Box>
                                    )}
                                </Paper>
                                
                                {/* ✅ FASE 3: SECCIÓN: MODO DE TRABAJO */}
                                <Paper className={classes.formSection}>
                                    <Typography className={classes.sectionTitle}>
                                        <Settings style={{ marginRight: 8 }} />
                                        {i18n.t("promptModal.sections.workMode")}
                                    </Typography>
                                    <FormControl fullWidth margin="dense" variant="outlined" className={`${classes.formField} ${classes.formControlDynamic}`}>
                                        <InputLabel>{i18n.t("promptModal.fields.workModeLabel")}</InputLabel>
                                        <Select
                                            key={`workmode-select-${promptId}-${selectedWorkMode}`}
                                            label={i18n.t("promptModal.fields.workModeLabel")}
                                            value={selectedWorkMode || "layers"}
                                            onChange={(e) => {
                                                console.log("[PromptModal] Cambiando workMode de", selectedWorkMode, "a", e.target.value);
                                                setSelectedWorkMode(e.target.value);
                                            }}
                                        >
                                            <MenuItem value="layers">
                                                <Box display="flex" alignItems="center">
                                                    <Layers style={{ marginRight: 8 }} />
                                                    <Typography>{i18n.t("promptModal.fields.workModeLayers")}</Typography>
                                                </Box>
                                            </MenuItem>
                                            <MenuItem value="pdfs">
                                                <Box display="flex" alignItems="center">
                                                    <Description style={{ marginRight: 8 }} />
                                                    <Typography>{i18n.t("promptModal.fields.workModePdfs")}</Typography>
                                                </Box>
                                            </MenuItem>
                                        </Select>
                                        <FormHelperText>
                                            {selectedWorkMode === 'layers' 
                                                ? i18n.t("promptModal.fields.workModeHintLayers") 
                                                : i18n.t("promptModal.fields.workModeHintPdfs")}
                                        </FormHelperText>
                                    </FormControl>
                                </Paper>
                                
                                {/* ✅ FASE 3: SECCIÓN CONDICIONAL: LAYERS O PDFs */}
                                {selectedWorkMode === 'layers' ? (
                                    <Paper className={classes.formSection}>
                                        <PromptLayersManager
                                            promptId={promptId}
                                            companyId={user?.companyId}
                                            onLayersChange={(layers) => {
                                                // Callback opcional para cuando cambien las layers
                                                console.log("Layers actualizadas:", layers.length);
                                            }}
                                        />
                                    </Paper>
                                ) : (
                                    <Paper className={classes.formSection}>
                                        <PromptDocumentsManager
                                            promptId={promptId}
                                            companyId={user?.companyId}
                                        />
                                    </Paper>
                                )}
                            </DialogContent>
                            
                            <DialogActions className={classes.dialogActions}>
                                <Button
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                    className={classes.cancelButton}
                                >
                                    {i18n.t("promptModal.buttons.cancelUpper")}
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={classes.saveButton}
                                >
                                    {promptId
                                        ? `${i18n.t("promptModal.buttons.okEdit")}`
                                        : `${i18n.t("promptModal.buttons.okAdd")}`}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={24}
                                            className={classes.buttonProgress}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                        );
                    }}
                </Formik>
            </Dialog>
            
            {/* ✅ MODAL PARA AGREGAR URL EXTERNA */}
            <Dialog
                open={urlModalOpen}
                onClose={() => setUrlModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle className={classes.dialogTitle}>
                    <Box display="flex" alignItems="center">
                        <LinkIcon style={{ marginRight: 12, fontSize: 28 }} />
                        <Typography variant="h6">
                            {i18n.t("promptModal.urlDialog.title")}
                        </Typography>
                    </Box>
                </DialogTitle>
                <UrlModalContent
                    onAdd={handleAddUrl}
                    onClose={() => setUrlModalOpen(false)}
                    loading={loadingMedia}
                    classes={classes}
                />
            </Dialog>
            
            {/* ✅ MODAL AMPLIADO PARA EDITAR PROMPT */}
            {expandedPromptModalOpen && (
                <ExpandedPromptModal
                    open={expandedPromptModalOpen}
                    promptText={expandedPromptText}
                    onClose={() => {
                        if (expandedPromptChanged) {
                            if (window.confirm(i18n.t("promptModal.expanded.confirmClose"))) {
                                handleSaveExpandedPrompt();
                            } else {
                                setExpandedPromptModalOpen(false);
                                setExpandedPromptChanged(false);
                            }
                        } else {
                            setExpandedPromptModalOpen(false);
                        }
                    }}
                    onSave={(newText) => {
                        // Actualizar el campo del formulario usando setFieldValue de Formik
                        // Necesitamos pasar setFieldValue desde el render de Formik
                        handleSaveExpandedPrompt(newText);
                    }}
                    onChange={(newText) => {
                        setExpandedPromptText(newText);
                        setExpandedPromptChanged(true);
                    }}
                    hasChanges={expandedPromptChanged}
                    classes={classes}
                />
            )}
        </div>
    );
    
    // ✅ FUNCIÓN PARA GUARDAR CAMBIOS DEL MODAL AMPLIADO
    function handleSaveExpandedPrompt(newText = null) {
        const textToSave = newText || expandedPromptText;
        // Usar setFieldValue de Formik si está disponible
        if (window.setFieldValuePrompt) {
            window.setFieldValuePrompt('prompt', textToSave);
        } else {
            // Fallback: actualizar directamente el campo
            const formikForm = document.querySelector('form');
            if (formikForm) {
                const promptInput = formikForm.querySelector('textarea[name="prompt"]');
                if (promptInput) {
                    const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
                        window.HTMLTextAreaElement.prototype,
                        "value"
                    )?.set;
                    if (nativeInputValueSetter) {
                        nativeInputValueSetter.call(promptInput, textToSave);
                    }
                    const event = new Event('input', { bubbles: true });
                    promptInput.dispatchEvent(event);
                }
            }
        }
        setExpandedPromptModalOpen(false);
        setExpandedPromptChanged(false);
        toast.success(i18n.t("promptModal.expanded.toastSaved"));
    }
};

// ✅ COMPONENTE SEPARADO PARA EL MODAL AMPLIADO DE PROMPT
const ExpandedPromptModal = ({ open, promptText, onClose, onSave, onChange, hasChanges, classes }) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            className={classes.expandedPromptDialog}
            maxWidth={false}
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">
                        {i18n.t("promptModal.expanded.title")}
                    </Typography>
                    {hasChanges && (
                        <Chip 
                            label={i18n.t("promptModal.expanded.unsavedChip")} 
                            style={{ backgroundColor: '#ff9800', color: '#fff' }}
                            size="small"
                        />
                    )}
                </Box>
            </DialogTitle>
            <DialogContent className={classes.expandedPromptContent}>
                <Box display="flex" flexDirection="column" height="100%">
                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        {i18n.t("promptModal.expanded.intro")}
                    </Typography>
                    
                    <TextField
                        multiline
                        fullWidth
                        variant="outlined"
                        value={promptText}
                        onChange={(e) => onChange(e.target.value)}
                        className={classes.expandedPromptEditor}
                        placeholder={i18n.t("promptModal.expanded.editorPlaceholder")}
                        InputProps={{
                            style: {
                                height: "100%",
                            },
                        }}
                    />
                    
                    <Box display="flex" justifyContent="flex-end" marginTop={1}>
                        <Typography variant="body2" color="textSecondary" style={{ fontWeight: 500 }}>
                            {i18n.t("promptModal.expanded.charCount", { count: promptText.length })}
                        </Typography>
                    </Box>
                    
                    <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 8 }}>
                        {i18n.t("promptModal.expanded.previewTitle")}
                    </Typography>
                    
                    <Box 
                        className={classes.expandedPromptPreview}
                        dangerouslySetInnerHTML={{
                            __html: formatPromptPreview(promptText)
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button
                    onClick={onClose}
                    className={classes.cancelButton}
                >
                    {i18n.t("promptModal.buttons.cancel")}
                </Button>
                <Button
                    onClick={() => onSave(promptText)}
                    className={classes.saveButton}
                    disabled={!hasChanges}
                >
                    {i18n.t("promptModal.buttons.saveChanges")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// ✅ FUNCIÓN PARA FORMATEAR LA VISTA PREVIA CON RESALTADO
const formatPromptPreview = (text) => {
    if (!text) return "";
    
    // Escapar HTML primero
    let html = text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\n/g, '<br>');
    
    // Resaltar sintaxis [MEDIA: nombrearchivo.ext]
    html = html.replace(
        /\[MEDIA:\s*([^\]]+)\]/gi,
        '<span style="background-color: #dbeafe; color: #1e40af; padding: 2px 6px; border-radius: 4px; font-weight: bold; font-family: \'Courier New\', monospace;">[MEDIA: $1]</span>'
    );
    
    // Resaltar secciones (líneas que empiezan con ** o #)
    html = html.replace(
        /(\*\*[^*]+\*\*|#{1,6}\s+[^\n]+)/g,
        '<span style="background-color: #d1fae5; color: #065f46; padding: 4px 8px; border-radius: 4px; font-weight: bold; display: inline-block; margin-bottom: 4px;">$1</span>'
    );
    
    return html;
};

// ✅ COMPONENTE SEPARADO PARA EL MODAL DE URL
const UrlModalContent = ({ onAdd, onClose, loading, classes }) => {
    const [url, setUrl] = React.useState("");
    const [caption, setCaption] = React.useState("");

    const handleSubmit = () => {
        if (url && url.trim()) {
            onAdd(url.trim(), caption.trim() || null);
            setUrl("");
            setCaption("");
        }
    };

    return (
        <>
            <DialogContent className={classes.dialogContent}>
                <TextField
                    label={i18n.t("promptModal.urlDialog.urlLabel")}
                    variant="outlined"
                    fullWidth
                    margin="dense"
                    className={classes.formField}
                    placeholder={i18n.t("promptModal.urlDialog.urlPlaceholder")}
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    onKeyPress={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSubmit();
                        }
                    }}
                />
                <TextField
                    label={i18n.t("promptModal.urlDialog.captionLabel")}
                    variant="outlined"
                    fullWidth
                    margin="dense"
                    className={classes.formField}
                    placeholder={i18n.t("promptModal.urlDialog.captionPlaceholder")}
                    multiline
                    rows={2}
                    value={caption}
                    onChange={(e) => setCaption(e.target.value)}
                />
                <Box className={classes.infoBox} style={{ marginTop: 16 }}>
                    <Typography className={classes.infoText} style={{ fontSize: "12px" }}>
                        {i18n.t("promptModal.urlDialog.hintPublic")}
                    </Typography>
                </Box>
            </DialogContent>
            <DialogActions className={classes.dialogActions}>
                <Button
                    onClick={onClose}
                    className={classes.cancelButton}
                >
                    {i18n.t("promptModal.urlDialog.close")}
                </Button>
                <Button
                    onClick={handleSubmit}
                    className={classes.saveButton}
                    disabled={loading || !url.trim()}
                >
                    {i18n.t("promptModal.urlDialog.add")}
                </Button>
            </DialogActions>
        </>
    );
};

export default PromptModal;